//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { useInstagramMessagesAccess } from '@account-settings/hooks/useInstagramMessagesAccess';
import {
  HistoricalUrlsResponse,
  RIGHTS_STATUS,
  RightsRequestCommunicationType,
} from '@apis/rights-management/rightsManagementApiModels';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { RightsRequestOptions } from '@legacy-components/common/universal-image-actions/constants/universalImageActionsRightsRequestContstants';
import { IRightsOption } from '@legacy-components/rights-management/request-rights-model';
import Cuid from '@models/Cuid';
import { SocialNetwork } from '@models/SocialNetwork';
import { getClientId } from '@redux/selectors/getClientId';
import { UnexpectedErrorAlert } from '@uia/UnexpectedErrorAlert';
import { useMediaMetadataQuery } from '@uia/details/hooks/useMediaMetadataQuery';
import { ChooseMessageType } from '@uia/rights/choose-message/ChooseMessageType';
import {
  FB_IG_NETWORKS,
  RIGHTS_REQUEST_EXTERNAL_APPROVAL_COMMUNICATION_TYPES,
  TIKTOK_RIGHTS_REQUEST_COPY_CHARACTER_LIMIT,
} from '@uia/rights/constants/uiaRightsConstants';
import { InstagramRightsRequest } from '@uia/rights/forms/InstagramRightsRequest';
import { RequestErrorMessages } from '@uia/rights/forms/RequestErrorMessages';
import { RequestText, RequestTextSource } from '@uia/rights/forms/RequestText';
import { RightsHashtagSelector } from '@uia/rights/forms/RightsHashtagSelector';
import { TikTokRightsRequest } from '@uia/rights/forms/TikTokRightsRequest';
import { RightsGalleryText } from '@uia/rights/gallery/RightsGalleryText';
import { useGenerativeAiRightsRequestQuery } from '@uia/rights/hooks/useGenerativeAiRightsRequestQuery';
import { useIsReRequestRequiredForMissingTCUrl } from '@uia/rights/hooks/useIsReRequestRequiredForMissingTCUrl';
import { useRightsConfigurationsQuery } from '@uia/rights/hooks/useRightsConfigurationsQuery';
import { useRightsHistoricalUrlsQuery } from '@uia/rights/hooks/useRightsHistoricalUrlsQuery';
import { useShouldShowCopyRightsFlowQuery } from '@uia/rights/hooks/useShouldShowCopyRightsFlowQuery';
import { DmRightsRequestConfigInfo } from '@uia/rights/info/DmRightsRequestConfigInfo';
import {
  getIsRightsRequestTermsAndConditionsEnabled,
  getIsTikTokMentionedRightsEnabled,
} from '@uia/rights/selectors/getUIARights';
import { getRightsRequestDetailsWithSyndicationCheck } from '@uia/rights/utils/rightsRequestUtils';
import { checkIsStatusInitiated } from '@uia/rights/utils/statusUtils';
import { isDefined } from '@utils/filterPredicates';

function hasNoHistoricalUrls(historicalUrls: HistoricalUrlsResponse) {
  return historicalUrls.length < 1;
}

export interface RequestContentProps {
  approvalPhrase: string | undefined;
  communicationType: RightsRequestCommunicationType | undefined;
  cuid: Cuid;
  onChangeOption: (option: IRightsOption) => void;
  requestTextIndex: number;
  requestTextSource: RequestTextSource;
  requestType: RightsRequestOptions;
  rightsRequestText: string | undefined;
  selectedOption?: IRightsOption;
  setApprovalPhrase: (approvalPhrase: string) => void;
  setRequestTextIndex: (value: number) => void;
  setRequestTextSource: (value: RequestTextSource) => void;
  setRightsRequestText: (rightsRequestText: string) => void;
  setSelectedOption: (value: IRightsOption) => void;
}

export const RequestContent = ({
  approvalPhrase,
  communicationType,
  cuid,
  onChangeOption,
  requestTextIndex,
  requestTextSource,
  requestType,
  rightsRequestText,
  selectedOption,
  setApprovalPhrase,
  setRequestTextIndex,
  setRequestTextSource,
  setRightsRequestText,
  setSelectedOption,
}: RequestContentProps) => {
  const clientId = useSelector(getClientId);

  const isRightsRequestTermsAndConditionsEnabled = useSelector(
    getIsRightsRequestTermsAndConditionsEnabled,
  );

  const isTikTokMentionedRightsEnabled = useSelector(
    getIsTikTokMentionedRightsEnabled,
  );

  const {
    data: metadata,
    isError: isErrorMetadata,
    isPending: isPendingMetadata,
  } = useMediaMetadataQuery(cuid);

  const { isError: isMessagesAccessStatusError } = useInstagramMessagesAccess();

  const { isFetching: isGeneratingText } = useGenerativeAiRightsRequestQuery({
    approvalPhrase,
    cuid,
    rightsRequestText,
  });

  const {
    data: shouldShowCopyRightsFlow,
    isError: isErrorCopyrightsShow,
    isPending: isPendingCopyrightsShow,
  } = useShouldShowCopyRightsFlowQuery(
    Cuid.toPartial(cuid),
    metadata?.rightsRequestStatus.status,
  );

  const {
    data: isMissingHistoricalTCUrl,
    isError: isErrorHistoricalUrls,
    isPending: isPendingHistoricalUrls,
  } = useRightsHistoricalUrlsQuery(
    (data) =>
      isRightsRequestTermsAndConditionsEnabled && hasNoHistoricalUrls(data),
  );

  const {
    isError: isErrorRequestRequired,
    isLoading: isLoadingRequestRequired,
    isReRequestRequiredForMissingTCUrl,
  } = useIsReRequestRequiredForMissingTCUrl({
    communicationType: metadata?.rightsRequestStatus.communicationType,
    clientId,
    cuid,
    rightsRequestText,
    status: metadata?.rightsRequestStatus.status,
  });

  const { isError: isErrorRightsConfigs, isPending: isLoadingRightsConfigs } =
    useRightsConfigurationsQuery(Cuid.toPartial(cuid), requestType);

  const showInstagramMessagesAccessError =
    isMessagesAccessStatusError && requestType === RightsRequestOptions.DM;

  const hasRequestErrors =
    isErrorHistoricalUrls ||
    isErrorRequestRequired ||
    isErrorRightsConfigs ||
    isErrorMetadata ||
    isErrorCopyrightsShow ||
    showInstagramMessagesAccessError;

  if (hasRequestErrors) {
    return <UnexpectedErrorAlert />;
  }

  const isLoading =
    isPendingHistoricalUrls ||
    isLoadingRequestRequired ||
    isPendingMetadata ||
    isPendingCopyrightsShow ||
    isLoadingRightsConfigs;

  if (isLoading) {
    return (
      <Wrapper>
        <CenteredSpinner />
      </Wrapper>
    );
  }

  const { status } = getRightsRequestDetailsWithSyndicationCheck(metadata);

  const areRightsAlreadyRequested = checkIsStatusInitiated(status);

  const isTextDisabled =
    isGeneratingText || !!isMissingHistoricalTCUrl || areRightsAlreadyRequested;

  const isMetaNetwork = FB_IG_NETWORKS.includes(
    metadata.socialNetwork ?? SocialNetwork.Unknown,
  );

  const isTikTokNetwork = metadata.socialNetwork === SocialNetwork.TikTok;

  const shouldRenderIgRequest =
    requestType === RightsRequestOptions.DM
      ? isMetaNetwork
      : isMetaNetwork && shouldShowCopyRightsFlow;

  const shouldRenderTikTokRequest = isTikTokNetwork && shouldShowCopyRightsFlow;
  const shouldShowDmRightsConfigInfo =
    requestType === RightsRequestOptions.DM &&
    status !== RIGHTS_STATUS.APPROVED;

  return (
    <Wrapper>
      {shouldShowDmRightsConfigInfo && <DmRightsRequestConfigInfo />}
      <ChooseMessageType
        cuid={cuid}
        requestType={requestType}
        rightsRequestText={rightsRequestText}
        selectedOption={selectedOption}
        setSelectedOption={onChangeOption}
      />
      <RequestText
        approvalPhrase={approvalPhrase}
        characterLimit={
          isTikTokNetwork
            ? TIKTOK_RIGHTS_REQUEST_COPY_CHARACTER_LIMIT
            : undefined
        }
        communicationType={communicationType}
        cuid={cuid}
        hasMultipleRequestTexts={
          (selectedOption?.rightsRequestData.data.length ?? 0) > 0
        }
        isDisabled={isTextDisabled}
        requestTextSource={requestTextSource}
        requestType={requestType}
        rightsRequestText={rightsRequestText}
        setNextRequestText={() => {
          const data = selectedOption?.rightsRequestData.data;
          if (isDefined(data)) {
            const newIndex =
              requestTextIndex + 1 >= data.length ? 0 : requestTextIndex + 1;
            setRequestTextIndex(newIndex);
            if (isDefined(data[newIndex])) {
              setRightsRequestText(data[newIndex].message);
            }
          }
        }}
        setRequestTextSource={setRequestTextSource}
        setRightsRequestText={setRightsRequestText}
      />
      <RightsHashtagSelector
        isDisabled={areRightsAlreadyRequested}
        onChange={setApprovalPhrase}
        rightsRequestText={rightsRequestText}
        value={approvalPhrase}
      />
      <RightsGalleryText cuid={cuid} />
      {shouldRenderIgRequest && (
        <InstagramRightsRequest
          approvalPhrase={approvalPhrase}
          cuid={cuid}
          isReRequestRequiredForMissingTCUrl={
            isReRequestRequiredForMissingTCUrl
          }
          requestType={requestType}
          rightsRequestText={rightsRequestText}
          setRightsRequestText={setRightsRequestText}
          setSelectedOption={setSelectedOption}
        />
      )}
      {shouldRenderTikTokRequest && isTikTokMentionedRightsEnabled && (
        <TikTokRightsRequest
          approvalPhrase={approvalPhrase}
          cuid={cuid}
          isReRequestRequiredForMissingTCUrl={
            isReRequestRequiredForMissingTCUrl
          }
          requestType={requestType}
          rightsRequestText={rightsRequestText}
          setRightsRequestText={setRightsRequestText}
          setSelectedOption={setSelectedOption}
        />
      )}

      {(!isDefined(communicationType) ||
        (isDefined(communicationType) &&
          !RIGHTS_REQUEST_EXTERNAL_APPROVAL_COMMUNICATION_TYPES.includes(
            communicationType,
          ))) && (
        <RequestErrorMessages
          isMissingHistoricalTCUrl={isMissingHistoricalTCUrl}
          isReRequestRequiredForMissingTCUrl={
            isReRequestRequiredForMissingTCUrl
          }
          rightsRequestText={rightsRequestText}
        />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[3]};
    padding: ${theme.spacings[3]};
  `,
);
